import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useAutosave - A hook for auto-saving drafts to localStorage
 * 
 * @param {string} key - Unique key for localStorage
 * @param {any} initialValue - Initial value if no saved draft exists
 * @param {number} debounceMs - Debounce time in milliseconds (default: 1000)
 * @returns {[any, function, function, boolean]} - [value, setValue, clearDraft, hasDraft]
 */
export function useAutosave(key, initialValue, debounceMs = 1000) {
  // Get initial value from localStorage or use provided initialValue
  const [value, setValue] = useState(() => {
    try {
      const savedItem = localStorage.getItem(`clientkit_draft_${key}`);
      if (savedItem) {
        const parsed = JSON.parse(savedItem);
        // Check if draft is less than 7 days old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
          return parsed.data;
        }
      }
    } catch (e) {
      console.warn('Failed to load draft:', e);
    }
    return initialValue;
  });
  
  const [hasDraft, setHasDraft] = useState(() => {
    try {
      return !!localStorage.getItem(`clientkit_draft_${key}`);
    } catch {
      return false;
    }
  });
  
  const timeoutRef = useRef(null);
  
  // Debounced save to localStorage
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      try {
        // Only save if value has meaningful content
        const hasContent = typeof value === 'string' 
          ? value.trim().length > 0 
          : value && Object.keys(value).some(k => value[k]);
        
        if (hasContent) {
          localStorage.setItem(`clientkit_draft_${key}`, JSON.stringify({
            data: value,
            timestamp: Date.now()
          }));
          setHasDraft(true);
        }
      } catch (e) {
        console.warn('Failed to save draft:', e);
      }
    }, debounceMs);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, key, debounceMs]);
  
  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(`clientkit_draft_${key}`);
      setHasDraft(false);
    } catch (e) {
      console.warn('Failed to clear draft:', e);
    }
  }, [key]);
  
  return [value, setValue, clearDraft, hasDraft];
}

/**
 * useFormAutosave - Auto-save for form objects with multiple fields
 * 
 * @param {string} key - Unique key for localStorage  
 * @param {object} initialValues - Initial form values
 * @param {number} debounceMs - Debounce time in milliseconds
 * @returns {object} - { values, setField, setValues, clearDraft, hasDraft, restoreDefaults }
 */
export function useFormAutosave(key, initialValues, debounceMs = 1000) {
  const [values, setValues, clearDraft, hasDraft] = useAutosave(key, initialValues, debounceMs);
  
  const setField = useCallback((field, value) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setValues]);
  
  const restoreDefaults = useCallback(() => {
    setValues(initialValues);
    clearDraft();
  }, [initialValues, setValues, clearDraft]);
  
  return {
    values,
    setField,
    setValues,
    clearDraft,
    hasDraft,
    restoreDefaults
  };
}

export default useAutosave;
